import React, { memo, useEffect, useRef } from "react";

import { CoreLayoutV2, isAndroidPlatform } from "@swiggy-private/core-ui";

import { ToingHeader } from "@cart-client/components/toing-header";
import { CommonUIWrapperProps } from "@cart-client/interfaces/Cart";

import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import {
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    allowInsetsForAndroid,
} from "@swiggy-private/checkout-core";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";

const CONTENT_EXTRA_PADDING_BOTTOM = 60;
interface ToingUIWrapperProps extends CommonUIWrapperProps {
    restaurantName?: string;
    isCartError?: boolean;
    isCartLoading?: boolean;
    isEmptyCart?: boolean;
    enableAnimatedSavingsStrip?: boolean;
    openHalfCard?: VoidFunction;
    isFreeCashApplicable?: boolean;
    uploadId?: string;
}

const ToingUIWrapper = React.forwardRef<HTMLDivElement, ToingUIWrapperProps>(
    (props: ToingUIWrapperProps, ref): React.ReactElement => {
        const theme = useTheme();
        const headerRef = useRef<HTMLDivElement>(null);

        const getContainerBackgroundColor = () => {
            const colors = theme.colors[theme.colorMode];

            if (props.restaurantName) {
                return colors.primary;
            }
            if (props.isCartError || props.isEmptyCart) {
                return colors.background_Primary;
            }
            // eslint-disable-next-line @typescript-eslint/naming-convention
            return colors.background_Secondary;
        };

        useEffect(() => {
            const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
            const shouldChangeStatusBar = !isAndroidPlatform(platform) || allowInsetsForAndroid();

            if (shouldChangeStatusBar) {
                getBridgeSdk()
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    .changeStatusBarColor({
                        style: theme.colors[theme.colorMode].special_primary,
                        statusBarMode: "lightContent",
                        bottomBarMode: "default",
                    })
                    .catch(() => void 0);
            }

            return () => {
                updateStatusBarCleanup();
            };
        }, []);

        return (
            <CoreLayoutV2
                containerStyleProps={{
                    backgroundColor: getContainerBackgroundColor(),
                }}
            >
                <>
                    <CoreLayoutV2.Header
                        containerStyleProps={{
                            background: "transparent",
                        }}
                        dataTestId={COMMON_HEADER_TEST_IDS.CART_HEADER_PARENT}
                        containerRef={headerRef}
                    >
                        <ToingHeader
                            onBack={props.onBack}
                            restaurantName={props.restaurantName}
                            isCartError={props.isCartError}
                            isCartLoading={props.isCartLoading}
                            isEmptyCart={!!props.isEmptyCart}
                            isPageScrolled={props.isPageScrolled}
                            showAddressWidgetTop={props.showAddressWidgetTop}
                            openHalfCard={props.openHalfCard}
                            isFreeCashApplicable={props.isFreeCashApplicable}
                            uploadId={props.uploadId}
                        >
                            {props.header}
                        </ToingHeader>
                    </CoreLayoutV2.Header>
                    <CoreLayoutV2.Content
                        containerStyleProps={{
                            backgroundColor:
                                props.isCartError || props.isEmptyCart
                                    ? "transparent"
                                    : // eslint-disable-next-line @typescript-eslint/naming-convention
                                      theme.colors[theme.colorMode].background_Secondary,
                            extraBottomPadding:
                                props.isEmptyCart || props.isCartError ? 0 : CONTENT_EXTRA_PADDING_BOTTOM,
                        }}
                        ref={ref}
                        // This is used to get the height of the header and adjust content height accordingly
                        headerRef={headerRef}
                        contentId={props.containerId}
                    >
                        {props.content}
                    </CoreLayoutV2.Content>
                </>
            </CoreLayoutV2>
        );
    },
);

if (__DEV__) ToingUIWrapper.displayName = "ToingUIWrapper";

export default memo(ToingUIWrapper);
